/**
 * Canvas engine for the homepage map: 32.677 sample points across all German
 * postcode areas fold in from a grid, then new register entries pulse at their
 * town and send a ripple through the dots around them. Pure DOM/canvas, no
 * React; the component owns the elements and receives events via onEvent.
 */

export interface LiveMapData {
  aspect: number;
  /** Uint16 pairs (x, y) as base64, normalised to 0..65535. */
  dots: string;
  plzCodes: string;
  plzXY: string;
  plzKreis: string;
  kreise: string[];
  /** [name, city, register court, register number, x, y] */
  companies: [string, string, string, string, number, number][];
}

export type LiveEventKey =
  | 'newRegistration'
  | 'newDirector'
  | 'shareholderList'
  | 'annualAccounts'
  | 'procuration'
  | 'articles'
  | 'seatMoved'
  | 'capitalRaised'
  | 'newAddress'
  | 'registerUpdate';

export interface LiveEvent {
  id: number;
  name: string;
  city: string;
  /** Register reference — the company page link (company-href.ts). */
  registerCourt: string;
  registerType: string;
  registerId: string;
  key: LiveEventKey;
  /** Simulated events only: seconds between publication and delivery. */
  lag?: number;
  /** Real events: when we fetched it; simulated: the made-up publication. Epoch ms. */
  at: number;
  animate: boolean;
}

/** A real arrival handed to the engine (LiveMapOptions.nextEvent). */
export type FeedEvent = Omit<LiveEvent, 'id' | 'animate' | 'lag'> & { postcode: string | null };

export interface LiveMapOptions {
  mapEl: HTMLElement;
  base: HTMLCanvasElement;
  fx: HTMLCanvasElement;
  data: LiveMapData;
  reduce: boolean;
  font: string;
  /** Dot and label colour, and the colour text is knocked out of. */
  ink: string;
  paper: string;
  onEvent: (event: LiveEvent) => void;
  /** Events keep flowing while the hero or the map is on screen. */
  isHeroVisible: () => boolean;
  /** Real events: the next arrival to show, or null to wait. Without it the
   *  engine runs the illustrative simulation over data.companies. */
  nextEvent?: () => FeedEvent | null;
}

export interface LiveMapHandle {
  resize(): void;
  destroy(): void;
}

const EVENT_WEIGHTS: [LiveEventKey, number][] = [
  ['newRegistration', 4],
  ['newDirector', 5],
  ['shareholderList', 5],
  ['annualAccounts', 6],
  ['procuration', 3],
  ['articles', 3],
  ['seatMoved', 2],
  ['capitalRaised', 2],
  ['newAddress', 3],
];
const EVENT_TOTAL = EVENT_WEIGHTS.reduce((a, [, w]) => a + w, 0);

function pickEvent(): LiveEventKey {
  let r = Math.random() * EVENT_TOTAL;
  for (const [key, w] of EVENT_WEIGHTS) {
    r -= w;
    if (r < 0) return key;
  }
  return EVENT_WEIGHTS[0][0];
}

function decodeU16(b64: string): Uint16Array {
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return new Uint16Array(buf.buffer);
}

interface Ring { x: number; y: number; t: number; label: string }
interface Ripple { x: number; y: number; t: number }

export function startLiveMap(o: LiveMapOptions): LiveMapHandle {
  const { mapEl, base, fx, data, reduce, font, ink, paper } = o;
  const bctx = base.getContext('2d');
  const fctx = fx.getContext('2d');
  if (!bctx || !fctx) return { resize() {}, destroy() {} };

  const fine = window.matchMedia('(pointer: fine)').matches;
  const dots = decodeU16(data.dots);
  const plzXY = decodeU16(data.plzXY);
  const plzK = decodeU16(data.plzKreis);
  const plzCodes = data.plzCodes.split(',');
  const N = dots.length / 2;

  let W = 0;
  let H = 0;
  let settled = false;
  let started = false;
  let destroyed = false;

  // Start positions: the dots stand in a table grid, then fold into the map.
  const order = new Uint32Array(N);
  for (let i = 0; i < N; i++) order[i] = i;
  order.sort((a, b) => dots[a * 2 + 1] - dots[b * 2 + 1] || dots[a * 2] - dots[b * 2]);
  const sx = new Float32Array(N);
  const sy = new Float32Array(N);
  const dl = new Float32Array(N);
  {
    const cols = Math.round(Math.sqrt(N * data.aspect));
    const rows = Math.ceil(N / cols);
    for (let k = 0; k < N; k++) {
      const i = order[k];
      const r = (k / cols) | 0;
      const c = k % cols;
      sx[i] = (c + 0.5) / cols;
      sy[i] = (r + 0.5) / rows;
      dl[i] = (r / rows) * 0.55;
    }
  }

  // A new entry sends a ripple through the dots around it.
  const ripples: Ripple[] = [];
  const rings: Ring[] = [];
  const RIPPLE_LIFE = 1.8;
  interface Wave { X: number; Y: number; R: number; w: number; band: number; A0: number; A: number }

  function drawDots(t: number, waves?: Wave[]) {
    if (!bctx) return;
    bctx.clearRect(0, 0, W, H);
    const s = Math.max(1.2, W / 360);
    bctx.fillStyle = ink;
    bctx.globalAlpha = 0.86;
    for (let i = 0; i < N; i++) {
      let x = dots[i * 2] / 65535;
      let y = dots[i * 2 + 1] / 65535;
      if (waves) {
        let px = x * W;
        let py = y * H;
        let grow = 0;
        for (const w of waves) {
          const dx = px - w.X;
          const dy = py - w.Y;
          const d = Math.sqrt(dx * dx + dy * dy);
          const off = d - w.R;
          if (off > w.band || off < -w.band || d < 0.5) continue;
          const g = Math.exp(-(off * off) / (w.w * w.w)) * w.A;
          px += (dx / d) * g;
          py += (dy / d) * g;
          grow = Math.max(grow, g / w.A0);
        }
        const ss = s * (1 + 0.9 * grow);
        bctx.fillRect(px - ss / 2, py - ss / 2, ss, ss);
        continue;
      }
      if (t < 1) {
        const k = Math.min(1, Math.max(0, t * 1.55 - dl[i]));
        const e = k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;
        x = sx[i] + (x - sx[i]) * e;
        y = sy[i] + (y - sy[i]) * e;
      }
      bctx.fillRect(x * W - s / 2, y * H - s / 2, s, s);
    }
    bctx.globalAlpha = 1;
  }

  function sizeMap() {
    const r = mapEl.getBoundingClientRect();
    const d = Math.min(2, window.devicePixelRatio || 1);
    W = r.width;
    H = r.height;
    for (const c of [base, fx]) {
      c.width = Math.round(W * d);
      c.height = Math.round(H * d);
    }
    bctx?.setTransform(d, 0, 0, d, 0, 0);
    fctx?.setTransform(d, 0, 0, d, 0, 0);
    if (settled) drawDots(1);
    else if (!started) drawDots(0);
  }

  function runIntro() {
    started = true;
    if (reduce) {
      settled = true;
      drawDots(1);
      return;
    }
    const t0 = performance.now();
    const DUR = 2200;
    const step = (now: number) => {
      if (destroyed) return;
      const t = Math.min(1, (now - t0) / DUR);
      drawDots(t);
      if (t < 1) requestAnimationFrame(step);
      else settled = true;
    };
    requestAnimationFrame(step);
  }

  // Nearest postcode under the pointer, bucketed on a coarse grid.
  const G = 64;
  const grid: number[][] = Array.from({ length: G * G }, () => []);
  for (let i = 0; i < plzCodes.length; i++) {
    const gx = Math.min(G - 1, ((plzXY[i * 2] / 65536) * G) | 0);
    const gy = Math.min(G - 1, ((plzXY[i * 2 + 1] / 65536) * G) | 0);
    grid[gy * G + gx].push(i);
  }
  function nearestPLZ(nx: number, ny: number): number {
    const gx = Math.min(G - 1, Math.max(0, (nx * G) | 0));
    const gy = Math.min(G - 1, Math.max(0, (ny * G) | 0));
    let best = -1;
    let bd = Infinity;
    for (let r = 0; r <= 2; r++) {
      for (let y = gy - r; y <= gy + r; y++) {
        for (let x = gx - r; x <= gx + r; x++) {
          if (x < 0 || y < 0 || x >= G || y >= G) continue;
          for (const i of grid[y * G + x]) {
            const dx = plzXY[i * 2] / 65535 - nx;
            const dy = (plzXY[i * 2 + 1] / 65535 - ny) / data.aspect;
            const dd = dx * dx + dy * dy;
            if (dd < bd) {
              bd = dd;
              best = i;
            }
          }
        }
      }
      if (r >= 1 && best >= 0) break;
    }
    return bd < 0.0009 ? best : -1;
  }

  let mouse: { x: number; y: number } | null = null;
  const onMove = (e: PointerEvent) => {
    const r = mapEl.getBoundingClientRect();
    mouse = { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const onLeave = () => {
    mouse = null;
  };
  if (fine) {
    mapEl.addEventListener('pointermove', onMove);
    mapEl.addEventListener('pointerleave', onLeave);
  }

  let mapVisible = false;
  const visObs = new IntersectionObserver((es) => {
    mapVisible = es[0].isIntersecting;
  });
  visObs.observe(mapEl);
  const introObs = new IntersectionObserver(
    (es, ob) => {
      if (es[0].isIntersecting) {
        runIntro();
        ob.disconnect();
      }
    },
    { threshold: 0.35 },
  );
  introObs.observe(mapEl);

  let wavesDrawn = false;
  let raf = 0;
  function frame(t: number) {
    raf = requestAnimationFrame(frame);
    if (!mapVisible || !fctx) return;
    if (settled && !reduce) {
      for (let i = ripples.length - 1; i >= 0; i--) {
        if ((t - ripples[i].t) / 1000 > RIPPLE_LIFE) ripples.splice(i, 1);
      }
      if (ripples.length) {
        const A0 = Math.max(6, W / 55);
        const speed = Math.max(W, H) * 0.42;
        const wd = Math.max(10, W / 42);
        drawDots(
          1,
          ripples.map((r) => {
            const age = Math.max(0, (t - r.t) / 1000);
            const k = age / RIPPLE_LIFE;
            return { X: r.x * W, Y: r.y * H, R: age * speed, w: wd, band: wd * 3, A0, A: A0 * Math.pow(1 - k, 1.6) };
          }),
        );
        wavesDrawn = true;
      } else if (wavesDrawn) {
        drawDots(1);
        wavesDrawn = false;
      }
    }
    fctx.clearRect(0, 0, W, H);
    for (let i = rings.length - 1; i >= 0; i--) {
      const r = rings[i];
      const age = (t - r.t) / 1000;
      if (age > 2.8) {
        rings.splice(i, 1);
        continue;
      }
      const X = r.x * W;
      const Y = r.y * H;
      const k = Math.min(1, age / 1.3);
      fctx.strokeStyle = ink;
      fctx.lineWidth = 1.5;
      fctx.globalAlpha = 1 - k;
      fctx.beginPath();
      fctx.arc(X, Y, 5 + k * 34, 0, Math.PI * 2);
      fctx.stroke();
      fctx.globalAlpha = 1;
      fctx.fillStyle = ink;
      fctx.beginPath();
      fctx.arc(X, Y, 4.5, 0, Math.PI * 2);
      fctx.fill();
      if (age < 2.3) {
        fctx.font = `700 13px ${font}`;
        const tw = fctx.measureText(r.label).width;
        const lx = X + 12 + tw + 12 > W ? X - 16 - tw - 12 : X + 12;
        fctx.globalAlpha = Math.min(1, (2.3 - age) / 0.4);
        fctx.fillStyle = ink;
        fctx.fillRect(lx, Y - 11, tw + 12, 21);
        fctx.fillStyle = paper;
        fctx.fillText(r.label, lx + 6, Y + 4);
        fctx.globalAlpha = 1;
      }
    }
    if (mouse && settled) {
      fctx.strokeStyle = ink;
      fctx.lineWidth = 1;
      fctx.beginPath();
      fctx.moveTo(0, Math.round(mouse.y) + 0.5);
      fctx.lineTo(W, Math.round(mouse.y) + 0.5);
      fctx.moveTo(Math.round(mouse.x) + 0.5, 0);
      fctx.lineTo(Math.round(mouse.x) + 0.5, H);
      fctx.stroke();
      const i = nearestPLZ(mouse.x / W, mouse.y / H);
      if (i >= 0) {
        const label = `${plzCodes[i]} ${data.kreise[plzK[i]]}`;
        fctx.font = `700 13px ${font}`;
        const tw = fctx.measureText(label).width;
        let bx = mouse.x + 10;
        let by = mouse.y + 10;
        if (bx + tw + 14 > W) bx = mouse.x - 10 - tw - 14;
        if (by + 24 > H) by = mouse.y - 34;
        fctx.fillStyle = ink;
        fctx.fillRect(bx, by, tw + 14, 24);
        fctx.fillStyle = paper;
        fctx.fillText(label, bx + 7, by + 16);
      }
    }
  }

  // Register events: real arrivals from o.nextEvent, placed by postcode; or,
  // without it, real company names with illustrative events.
  const plzIndex = new Map(plzCodes.map((code, i) => [code, i]));
  let cIdx = 0;
  let nextId = 1;
  function pulse(x: number, y: number, label: string) {
    const pt = performance.now();
    rings.push({ x, y, t: pt, label });
    ripples.push({ x, y, t: pt });
  }
  function fireEvent(animate: boolean) {
    if (o.nextEvent) {
      const ev = o.nextEvent();
      if (!ev) return;
      const { postcode, ...rest } = ev;
      o.onEvent({ ...rest, id: nextId++, animate });
      const i = postcode ? plzIndex.get(postcode) : undefined;
      if (animate && i !== undefined) pulse(plzXY[i * 2] / 65535, plzXY[i * 2 + 1] / 65535, ev.city);
      return;
    }
    const [name, city, court, register, x, y] = data.companies[cIdx++ % data.companies.length];
    const [registerType = '', registerId = ''] = register.split(/\s+/, 2);
    const lag = 0.7 + Math.random() * 1.9;
    const key = pickEvent();
    o.onEvent({ id: nextId++, name, city, registerCourt: court, registerType, registerId, key, lag, at: Date.now() - lag * 1000, animate });
    if (animate) pulse(x / 65535, y / 65535, city);
  }
  for (let i = 0; i < 3; i++) fireEvent(false);
  let timer = 0;
  const loop = () => {
    if (destroyed) return;
    if (!document.hidden && (o.isHeroVisible() || mapVisible)) fireEvent(true);
    timer = window.setTimeout(loop, 1800 + Math.random() * 1700);
  };
  timer = window.setTimeout(loop, 1800);

  sizeMap();
  // Follow the map element's own size, not just window resizes: the box
  // settles after the first measure (the side column filling in, fonts), and
  // a canvas left at the old size is stretched by CSS, so the drawn crosshair
  // drifted off the cursor towards the bottom right.
  let roFrame = 0;
  const ro = new ResizeObserver(() => {
    cancelAnimationFrame(roFrame);
    roFrame = requestAnimationFrame(() => {
      const r = mapEl.getBoundingClientRect();
      if (Math.abs(r.width - W) > 0.5 || Math.abs(r.height - H) > 0.5) sizeMap();
    });
  });
  ro.observe(mapEl);
  raf = requestAnimationFrame(frame);

  return {
    resize: sizeMap,
    destroy() {
      destroyed = true;
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      visObs.disconnect();
      ro.disconnect();
      cancelAnimationFrame(roFrame);
      introObs.disconnect();
      mapEl.removeEventListener('pointermove', onMove);
      mapEl.removeEventListener('pointerleave', onLeave);
    },
  };
}
